import { useEffect, useMemo, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Search } from "lucide-react";

import { Kbd } from "./Kbd";

interface Command {
    id: string;
    label: string;
    group: string;
    keywords?: string;
    /** Either a route to navigate to, or an action callback. */
    to?: string;
    perform?: () => void;
}

const COMMANDS: ReadonlyArray<Command> = [
    { id: "go-home", label: "Dashboard", group: "Navigate", to: "/", keywords: "home overview" },
    { id: "go-iocs", label: "IoCs", group: "Navigate", to: "/iocs", keywords: "indicators of compromise" },
    { id: "go-rules", label: "Detection rules", group: "Navigate", to: "/rules", keywords: "yara sigma suricata" },
    { id: "go-workflows", label: "Workflows", group: "Navigate", to: "/workflows", keywords: "automation" },
    { id: "go-feeds", label: "Feeds", group: "Navigate", to: "/feeds", keywords: "ingestion sources" },
    { id: "go-tenants", label: "Tenants", group: "Navigate", to: "/tenants", keywords: "customers organizations" },
    { id: "go-licenses", label: "Licenses", group: "Navigate", to: "/licenses", keywords: "quotas plans" },
    { id: "go-settings", label: "Settings", group: "Navigate", to: "/settings", keywords: "config preferences" },
];

/**
 * The ⌘K command palette. Opens with ⌘K (Mac) / Ctrl+K (Windows/Linux),
 * closes with Escape. Fuzzy filter is a simple substring match for now;
 * swap for `cmdk` if we need scoring later.
 */
export function CommandBar(): React.ReactElement {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpen((v) => !v);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        if (open) {
            setQuery("");
            setActiveIndex(0);
            queueMicrotask(() => inputRef.current?.focus());
        }
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return COMMANDS;
        return COMMANDS.filter((c) =>
            (`${c.label} ${c.group} ${c.keywords ?? ""}`).toLowerCase().includes(q),
        );
    }, [query]);

    const grouped = useMemo(() => {
        const map = new Map<string, Command[]>();
        for (const c of filtered) {
            const list = map.get(c.group) ?? [];
            list.push(c);
            map.set(c.group, list);
        }
        return Array.from(map.entries());
    }, [filtered]);

    function runCommand(cmd: Command): void {
        setOpen(false);
        if (cmd.to) {
            void navigate({ to: cmd.to });
            return;
        }
        cmd.perform?.();
    }

    function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const target = filtered[activeIndex];
            if (target) runCommand(target);
        }
    }

    return (
        <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Portal>
                <Dialog.Overlay className="cmdbar-overlay">
                    <Dialog.Content className="cmdbar" onOpenAutoFocus={(e) => e.preventDefault()}>
                        <Dialog.Title className="visually-hidden">Command palette</Dialog.Title>
                        <Dialog.Description className="visually-hidden">
                            Search and execute commands
                        </Dialog.Description>
                        <div style={{ display: "flex", alignItems: "center", paddingLeft: 16 }}>
                            <Search size={16} aria-hidden style={{ color: "var(--color-text-subtle)" }} />
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setActiveIndex(0);
                                }}
                                onKeyDown={onInputKeyDown}
                                placeholder="Type a command or search…"
                                className="cmdbar__input"
                                style={{ paddingLeft: 12 }}
                            />
                        </div>
                        <ul className="cmdbar__list">
                            {grouped.length === 0 && (
                                <li className="cmdbar__group">No matches</li>
                            )}
                            {grouped.map(([group, items]) => (
                                <li key={group}>
                                    <div className="cmdbar__group">{group}</div>
                                    {items.map((item) => {
                                        const indexInFiltered = filtered.indexOf(item);
                                        const isActive = indexInFiltered === activeIndex;
                                        return (
                                            <button
                                                key={item.id}
                                                type="button"
                                                role="option"
                                                aria-selected={isActive}
                                                onMouseEnter={() => setActiveIndex(indexInFiltered)}
                                                onClick={() => runCommand(item)}
                                                className={`cmdbar__item ${isActive ? "cmdbar__item--active" : ""}`}
                                                style={{ width: "100%", textAlign: "left" }}
                                            >
                                                <ArrowRight size={14} aria-hidden style={{ opacity: 0.5 }} />
                                                <span>{item.label}</span>
                                                <span className="cmdbar__item__hint">
                                                    {isActive && <Kbd>↵</Kbd>}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </li>
                            ))}
                        </ul>
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
